import type { ReactNode } from 'react';
import { CircleHelp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/** Inline field error under a control — use with `fieldClass` + `aria-invalid`. */
export function FieldHint({ error }: { error?: string }) {
  if (!error) return null;
  return <p className="text-[11px] font-medium text-red-600">{error}</p>;
}

/** Asterisk for required labels. */
export function RequiredMark() {
  return <span className="text-red-500">*</span>;
}

/**
 * Field label with an optional help tooltip (icon button so it stays keyboard-accessible).
 * Wrap the surrounding form in TooltipProvider.
 */
export function FieldLabel({
  children,
  tip,
  required,
  className,
}: {
  children: ReactNode;
  tip?: string;
  required?: boolean;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium text-gray-600', className)}>
      {children}
      {required ? <RequiredMark /> : null}
      {tip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex rounded text-gray-400 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="About this field"
            >
              <CircleHelp className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[16rem] text-xs leading-snug">
            {tip}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  );
}

/** Merge base input classes with an error ring when validation fails. */
export function fieldClass(base: string, error?: string) {
  return cn(base, error && 'border-red-300 ring-1 ring-red-200 focus:ring-red-400');
}

/**
 * Clear one key from an errors map when the user edits that field.
 * Keeps submit-time validation without sticky messages.
 */
export function clearFieldError<E extends Record<string, string | undefined>>(
  errors: E,
  key: keyof E,
): E {
  if (!errors[key]) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
}
